// src/pages/HowItWorks.jsx

import React, { useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FilePlus,
  Users,
  MessageCircle,
  CheckCircle,
  DollarSign,
  Plus,
  Minus,
  ArrowRight,
} from "lucide-react";

import NavBar from "../components/layout/NavBar";
import MobileFooterSpacer from "../components/MobileFooterSpacer";

//================================================================//
//  DESIGN SYSTEM
//================================================================//

const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    // Step accent colors with shadows
    step1: { bg: "#eff6ff", color: "#2563eb", shadow: "0 4px 12px rgba(37, 99, 235, 0.2)" },
    step2: { bg: "#ecfdf5", color: "#059669", shadow: "0 4px 12px rgba(5, 150, 105, 0.2)" },
    step3: { bg: "#faf5ff", color: "#a855f7", shadow: "0 4px 12px rgba(168, 85, 247, 0.2)" },
    step4: { bg: "#fef3c7", color: "#d97706", shadow: "0 4px 12px rgba(215, 119, 6, 0.2)" },
    step5: { bg: "#fdf2f8", color: "#ec4899", shadow: "0 4px 12px rgba(236, 72, 153, 0.2)" },
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 4px 12px rgba(15, 23, 42, 0.06)",
    lg: "0 6px 16px rgba(15, 23, 42, 0.06)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  radius: "12px",
};

//================================================================//
//  ANIMATIONS & GLOBAL STYLES
//================================================================//

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideInUp = keyframes`from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); }`;

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${theme.colors.background};
    font-family: ${theme.font};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

//================================================================//
//  PAGE LAYOUT
//================================================================//

const PageWrapper = styled.div`
  background: ${theme.colors.background};
  padding-top: 64px;
  min-height: 100vh;
  animation: ${fadeIn} 0.5s ease;
  overflow-x: hidden;
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem 4rem;

  @media (max-width: 768px) {
    padding: 0 1rem 2rem;
  }
`;

//================================================================//
//  HERO SECTION
//================================================================//

const Hero = styled.section`
  text-align: center;
  padding: 4rem 1rem 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 2rem 0.5rem 2rem;

    &::before {
      width: 400px;
      height: 400px;
    }
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  color: ${theme.colors.textHeading};
  margin: 0;
  letter-spacing: -0.03em;
  line-height: 1.15;
`;

const GradientText = styled.span`
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border-radius: 6px;
  padding: 2px 6px;
`;

const Subtitle = styled.p`
  max-width: 600px;
  margin: 1.25rem auto 2rem;
  font-size: 1.2rem;
  color: ${theme.colors.textMuted};
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 1rem auto 1.5rem;
    padding: 0 0.5rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  border: 1px solid ${theme.colors.primary};
  background: ${theme.colors.primary};
  color: white;
  box-shadow: none;

  &:hover {
    background: ${theme.colors.primaryText};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
    justify-content: center;
    padding: 12px 24px;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border: 1px solid ${theme.colors.border};
  background: white;
  color: ${theme.colors.textBody};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
    justify-content: center;
    padding: 12px 24px;
  }
`;

//================================================================//
//  STEPS SECTION - Unified Timeline Design
//================================================================//

const StepsSection = styled.section`
  padding: 4rem 0;

  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const SectionSubtitle = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0 0.5rem;
  }
`;

const StepsTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  max-width: 700px;
  margin: 0 auto;
`;

const StepCard = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem 0;
  animation: ${slideInUp} 0.6s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: both;

  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

const StepNumber = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${props => props.$bgColor || theme.colors.primaryLight};
  color: ${props => props.$color || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.25rem;
  box-shadow: none;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 1.125rem;
    border-radius: 14px;
  }
`;

const StepContent = styled.div`
  flex: 1;
  padding-top: 8px;
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const StepText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 1rem;
  line-height: 1.65;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.55;
  }
`;

//================================================================//
//  TWO PATHS SECTION
//================================================================//

const PathsSection = styled.section`
  padding: 3rem 0 4rem;

  @media (max-width: 768px) {
    padding: 2rem 0 2.5rem;
  }
`;

const PathsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const PathCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: none;
  border: 1px solid ${theme.colors.border};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primaryLight};
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;

    &:hover {
      transform: none;
    }
  }
`;

const PathHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    gap: 0.75rem;
  }
`;

const PathIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    border-radius: 14px;
  }
`;

const PathTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const PathSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

const PathStep = styled.div`
  display: flex;
  gap: 1rem;
`;

const PathStepIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.$bgColor || theme.colors.primaryLight};
  color: ${props => props.$color || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }
`;

const PathStepContent = styled.div`
  flex: 1;
`;

const PathStepTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.textBody};
  margin: 0 0 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const PathStepText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

//================================================================//
//  FAQ SECTION
//================================================================//

const FAQSection = styled.section`
  padding: 3rem 0 2rem;

  @media (max-width: 768px) {
    padding: 2rem 0 1.5rem;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 800px;
  margin: 0 auto;
`;

const FAQCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: 12px;
  border: 1px solid ${theme.colors.border};
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.$open ? theme.colors.primaryLight : theme.colors.border};
  }
`;

const FAQButton = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${theme.colors.primaryLight};
  }

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
  }
`;

const FAQQuestion = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const FAQIconWrap = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${props => props.$open ? theme.colors.primaryLight : theme.colors.background};
  color: ${props => props.$open ? theme.colors.primary : theme.colors.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
`;

const FAQPanel = styled.div`
  padding: ${props => props.$open ? '0 1.5rem 1.25rem' : '0 1.5rem'};
  max-height: ${props => props.$open ? '300px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: ${props => props.$open ? '0 1.25rem 1rem' : '0 1.25rem'};
  }
`;

const FAQAnswer = styled.p`
  margin: 0;
  color: ${theme.colors.textMuted};
  font-size: 0.95rem;
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    line-height: 1.6;
  }
`;

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);

  return (
    <FAQCard $open={open}>
      <FAQButton onClick={() => setOpen(!open)} aria-expanded={open}>
        <FAQQuestion>{q}</FAQQuestion>
        <FAQIconWrap $open={open}>
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </FAQIconWrap>
      </FAQButton>
      <FAQPanel $open={open}>
        <FAQAnswer>{a}</FAQAnswer>
      </FAQPanel>
    </FAQCard>
  );
};

//================================================================//
//  CTA SECTION
//================================================================//

const CTASection = styled.section`
  padding: 3rem 0;

  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const CTACard = styled.div`
  background: ${theme.colors.surface};
  border-radius: 12px;
  padding: 2.5rem 1.5rem;
  border: 1px solid ${theme.colors.border};
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
    border-radius: 20px;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 1rem;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const CTAText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 1rem;
  margin: 0 0 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const CTAPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  border: 1px solid ${theme.colors.primary};
  background: ${theme.colors.primary};
  color: white;
  box-shadow: none;

  &:hover {
    background: ${theme.colors.primaryText};
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 260px;
    justify-content: center;
    padding: 12px 24px;
  }
`;

const CTASecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border: 1px solid ${theme.colors.border};
  background: transparent;
  color: ${theme.colors.textBody};

  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 260px;
    justify-content: center;
    padding: 12px 24px;
  }
`;

//================================================================//
//  MAIN COMPONENT
//================================================================//

const HowItWorks = ({ state }) => {
  const navigate = useNavigate();
  const user = state?.user;

  const steps = [
    {
      number: 1,
      title: "Post Your Task",
      text: "Describe what you need help with and set your budget. The clearer your description, the better offers you'll receive.",
      colors: theme.colors.step1,
    },
    {
      number: 2,
      title: "Receive Offers",
      text: "Skilled helpers will send offers with their pricing, approach, and timeline. Compare options at your own pace.",
      colors: theme.colors.step2,
    },
    {
      number: 3,
      title: "Choose Your Helper",
      text: "Review profiles, ratings, and proposals. Pick the person who feels right for your task.",
      colors: theme.colors.step3,
    },
    {
      number: 4,
      title: "Chat & Collaborate",
      text: "Use built-in messaging to discuss details, share files, and coordinate. Stay connected throughout the project.",
      colors: theme.colors.step4,
    },
    {
      number: 5,
      title: "Complete & Pay",
      text: "Mark the task complete when you're satisfied. Pay directly using cash, Venmo, PayPal, or any method you both prefer.",
      colors: theme.colors.step5,
    },
  ];

  const faqItems = [
    {
      q: "What is Doitly?",
      a: "Doitly is a marketplace where people post tasks and receive offers from helpers. You can compare proposals, chat, and complete tasks with confidence.",
    },
    {
      q: "How do I pay for completed work?",
      a: "Payment is handled directly between you and the helper after work completion. Popular methods include cash, Venmo, Zelle, PayPal, or bank transfer. Doitly doesn't process payments, giving you flexibility to choose what works best.",
    },
    {
      q: "Is it free to post a task?",
      a: "Yes, posting tasks is completely free. You only pay the helper directly once the work is completed to your satisfaction.",
    },
    {
      q: "How do I become a helper?",
      a: "Simply create an account and start browsing available tasks. When you find one that matches your skills, submit an offer with your pricing and approach.",
    },
    {
      q: "What if there's an issue during my task?",
      a: "Use the chat feature to communicate clearly with the other party. We recommend agreeing on milestones and deliverables upfront to prevent misunderstandings.",
    },
  ];

  return (
    <>
      <GlobalStyle />
      <PageWrapper>
        <NavBar user={user} />
        <ContentContainer>
          {/* Hero */}
          <Hero>
            <HeroContent>
              <Title>
                Get Any Task Done.{" "}
                <GradientText>Big or Small.</GradientText>
              </Title>
              <Subtitle>
                Post what you need, receive offers from local helpers, and get things done.
                It's the simplest way to get help with anything.
              </Subtitle>
              <HeroButtons>
                <PrimaryButton onClick={() => navigate("/posttask")}>
                  Post a Task <ArrowRight size={18} />
                </PrimaryButton>
                <SecondaryButton onClick={() => navigate("/tasks")}>
                  Browse Tasks
                </SecondaryButton>
              </HeroButtons>
            </HeroContent>
          </Hero>

          {/* How It Works Steps */}
          <StepsSection>
            <SectionHeader>
              <SectionTitle>How Doitly Works</SectionTitle>
              <SectionSubtitle>
                From posting your task to getting it done, here's the simple process.
              </SectionSubtitle>
            </SectionHeader>

            <StepsTimeline>
              {steps.map((step, idx) => (
                <StepCard key={step.number} $delay={`${idx * 0.1}s`}>
                  <StepNumber
                    $bgColor={step.colors.bg}
                    $color={step.colors.color}
                    $shadow={step.colors.shadow}
                  >
                    {step.number}
                  </StepNumber>
                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    <StepText>{step.text}</StepText>
                  </StepContent>
                </StepCard>
              ))}
            </StepsTimeline>
          </StepsSection>

          {/* Two Paths: Post vs Earn */}
          <PathsSection>
            <SectionHeader>
              <SectionTitle>Choose Your Path</SectionTitle>
              <SectionSubtitle>
                Whether you need help or want to earn money, Doitly has you covered.
              </SectionSubtitle>
            </SectionHeader>

            <PathsGrid>
              {/* Post a Task Path */}
              <PathCard>
                <PathHeader>
                  <PathIcon>
                    <FilePlus size={26} />
                  </PathIcon>
                  <PathTitle>Need Help?</PathTitle>
                </PathHeader>
                <PathSteps>
                  <PathStep>
                    <PathStepIcon $bgColor={theme.colors.step1.bg} $color={theme.colors.step1.color}>
                      <FilePlus size={18} />
                    </PathStepIcon>
                    <PathStepContent>
                      <PathStepTitle>Describe Your Task</PathStepTitle>
                      <PathStepText>Share details and set your budget. Be specific to get better offers.</PathStepText>
                    </PathStepContent>
                  </PathStep>
                  <PathStep>
                    <PathStepIcon $bgColor={theme.colors.step2.bg} $color={theme.colors.step2.color}>
                      <Users size={18} />
                    </PathStepIcon>
                    <PathStepContent>
                      <PathStepTitle>Review Offers</PathStepTitle>
                      <PathStepText>Compare proposals from skilled helpers and pick your favorite.</PathStepText>
                    </PathStepContent>
                  </PathStep>
                  <PathStep>
                    <PathStepIcon $bgColor={theme.colors.step3.bg} $color={theme.colors.step3.color}>
                      <MessageCircle size={18} />
                    </PathStepIcon>
                    <PathStepContent>
                      <PathStepTitle>Collaborate & Complete</PathStepTitle>
                      <PathStepText>Chat, share files, and track progress until you're satisfied.</PathStepText>
                    </PathStepContent>
                  </PathStep>
                </PathSteps>
              </PathCard>

              {/* Earn Money Path */}
              <PathCard>
                <PathHeader>
                  <PathIcon>
                    <DollarSign size={26} />
                  </PathIcon>
                  <PathTitle>Want to Earn?</PathTitle>
                </PathHeader>
                <PathSteps>
                  <PathStep>
                    <PathStepIcon $bgColor="#ecfdf5" $color="#059669">
                      <Users size={18} />
                    </PathStepIcon>
                    <PathStepContent>
                      <PathStepTitle>Browse Available Tasks</PathStepTitle>
                      <PathStepText>Find tasks that match your skills, location, and availability.</PathStepText>
                    </PathStepContent>
                  </PathStep>
                  <PathStep>
                    <PathStepIcon $bgColor="#fef3c7" $color="#d97706">
                      <FilePlus size={18} />
                    </PathStepIcon>
                    <PathStepContent>
                      <PathStepTitle>Submit Your Offer</PathStepTitle>
                      <PathStepText>Explain your approach and set your price. Stand out with a great proposal.</PathStepText>
                    </PathStepContent>
                  </PathStep>
                  <PathStep>
                    <PathStepIcon $bgColor="#fdf2f8" $color="#ec4899">
                      <CheckCircle size={18} />
                    </PathStepIcon>
                    <PathStepContent>
                      <PathStepTitle>Deliver & Get Paid</PathStepTitle>
                      <PathStepText>Complete the work, build your reputation, and receive payment directly.</PathStepText>
                    </PathStepContent>
                  </PathStep>
                </PathSteps>
              </PathCard>
            </PathsGrid>
          </PathsSection>

          {/* FAQ */}
          <FAQSection>
            <SectionHeader>
              <SectionTitle>Frequently Asked Questions</SectionTitle>
              <SectionSubtitle>
                Everything you need to know about using Doitly.
              </SectionSubtitle>
            </SectionHeader>

            <FAQList>
              {faqItems.map((item, idx) => (
                <FAQItem key={idx} q={item.q} a={item.a} />
              ))}
            </FAQList>
          </FAQSection>

          {/* CTA */}
          <CTASection>
            <CTACard>
              <CTAContent>
                <CTATitle>Ready to Get Started?</CTATitle>
                <CTAText>
                  Join thousands of people getting tasks done and earning money on their own terms.
                </CTAText>
                <CTAButtons>
                  <CTAPrimaryButton onClick={() => navigate("/posttask")}>
                    Post Your First Task
                  </CTAPrimaryButton>
                  <CTASecondaryButton onClick={() => navigate("/tasks")}>
                    Browse Tasks
                  </CTASecondaryButton>
                </CTAButtons>
              </CTAContent>
            </CTACard>
          </CTASection>

          <MobileFooterSpacer />
        </ContentContainer>
      </PageWrapper>
    </>
  );
};

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(HowItWorks);
